"use client";
import type { ProcessedCourse } from "@/common/course/types";
import type { Program } from "@/common/program/types";
import ProgramEnrollButton from "../../../components/ProgramEnrollButton";
import EmailOptIn from "@/components/EmailOptIn/EmailOptIn";
import useEmailOptIn from "../../../context/ProgramDetailContext/hooks/useEmailOptIn";
import { useProgramRedesignConfig } from "@/monarch/rules/programRedesignTraffic/useProgramRedesignTraffic";
import ProgramPricing from "@/components/ProgramDetailLayout/templates/components/ProgramPricing";
import { ProgramPricingData } from "@/lib/ecommerce/commercetools/client-only/getProgramPricingData";
import useTranslations from "@/i18n/useTranslations";

type ProgramEnrollProps = {
  courses: ProcessedCourse[];
  programData: Program;
  duration: string | null;
  skus: string[];
  pricingData: Partial<ProgramPricingData> | null;
};

export default function ProgramEnroll({
  courses,
  programData,
  duration,
  skus,
  pricingData,
}: ProgramEnrollProps) {
  const t = useTranslations("Common");
  const tProgram = useTranslations("Program");
  const { emailOptIn, setEmailOptIn } = useEmailOptIn();
  const { programEnrollCta, showSideRail } = useProgramRedesignConfig();
  const { partners, title } = programData;
  const count = courses.length;
  const originalPriceSize =
    programEnrollCta?.pricingTypographySize?.originalPrice || "5xl";
  const discountedPriceSize =
    programEnrollCta?.pricingTypographySize?.discountedPrice || "5xl";
  const strikethroughClass =
    programEnrollCta?.strikethroughToggle !== false ? "line-through" : "";

  return (
    <div
      id="program-enrolling-now-section"
      className="flex flex-col md:flex-row"
    >
      <div
        className={`bg-putty-400 py-10 px-8 ${showSideRail ? "md:py-10" : "md:py-28"}`}
      >
        <div
          className={`flex gap-4 justify-center md:justify-start ${showSideRail ? "flex-col" : "flex-col md:flex-row"}`}
        >
          <h2 className="m-0 text-3xl text-center md:text-left">
            {!programEnrollCta?.enrollingNowLanguage ||
            programEnrollCta.enrollingNowLanguage === "Enrolling Now"
              ? tProgram("enrollingNow")
              : programEnrollCta.enrollingNowLanguage}
          </h2>
          <div className={`${showSideRail && "md:max-w-max"}`}>
            <ProgramEnrollButton
              programData={programData}
              courses={courses}
              skus={skus}
            />
          </div>
        </div>
        <div className={`pt-6 ${showSideRail ? "" : "lg:pr-32"}`}>
          <EmailOptIn
            title={title || ""}
            partners={partners}
            optIn={emailOptIn}
            variant="secondary"
            toggleEmailOptIn={() => {
              setEmailOptIn(!emailOptIn);
            }}
            checkboxId="email-opt-in-program"
          />
        </div>
      </div>

      <div className="bg-primary-500 p-10 flex justify-end items-center text-white">
        <div className="flex flex-col">
          <div className="flex flex-col flex-col-reverse font-bold text-white italic mb-4 text-right">
            {pricingData ? (
              <ProgramPricing
                pricingData={pricingData}
                originalPriceClassName="mb-6"
                strikethroughClass={strikethroughClass}
                originalPriceSize={originalPriceSize}
                discountedPriceSize={discountedPriceSize}
                stackPricesInBanner={true}
              />
            ) : null}
          </div>
          <div className="font-bold text-right">
            {t("coursesInDuration", { count, duration: duration ?? "" })}
          </div>
          {/* TODO: Need to define behavior for this, and when it should show, hidden for now */}
          {/* <div className="mt-6 flex gap-1 justify-end text-sm items-center">
            <span>{t("financialAid")}</span>
            <Info svgProps={{ width: 18, height: 18 }} />
          </div> */}
        </div>
      </div>
    </div>
  );
}
